import { useState, type ReactNode } from "react";
import { api, errorMessage } from "../api/client.ts";
import type { ActivityItem, TeamData } from "../api/types.ts";
import { DataTable, type Column } from "../components/DataTable.tsx";
import { Dialog } from "../components/Dialog.tsx";
import { DateRange, useUrlFilters } from "../components/Filters.tsx";
import { Alert, Details, EmptyState, Hash, PageHeader, Time } from "../components/ui.tsx";
import { fmt, tr, useT } from "../i18n/index.tsx";
import { shorten } from "../lib/format.ts";
import { useAsync } from "../lib/hooks.ts";
import { usePagedList } from "../lib/paging.ts";
import { Pager } from "../components/Pager.tsx";
import { permissionLabel } from "../lib/permissions.ts";
import { useSession } from "../lib/session.tsx";

/** Audit action label; unknown actions: "foo.bar_baz" → "Foo bar baz". */
const label = (a: string) => (tr().activity.actions as Record<string, string>)[a] ?? a.replace(/[._]/g, " ").replace(/^\w/, (c) => c.toUpperCase());
/** Target type label; unknown types show the code. */
const targetLabel = (type: string) => (tr().activity.targets as Record<string, string>)[type] ?? type;
/** Extra detail key label; unknown keys: "foo_bar" → "foo bar". */
const detailLabel = (k: string) => (tr().activity.detailKeys as Record<string, string>)[k] ?? k.replace(/_/g, " ");

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === "object" && !Array.isArray(v);

/** One-line context for the list. */
function brief(d: unknown): string | null {
  if (!isObj(d)) return null;
  if ("reason" in d) return fmt(tr().activity.quote, { text: String(d.reason) });
  if ("email" in d && !("before" in d)) return String(d.email);
  if ("url" in d) return String(d.url);
  if ("before" in d || "after" in d) return tr().activity.seeChanges;
  return null;
}

/** Turns a value into readable text (brand ids → names, permission ids → labels, null → "organization's list"). */
function useFormatter(brands: Record<string, string>) {
  const t = useT().activity;
  return (key: string, v: unknown): ReactNode => {
    if (v === null || v === undefined) return <span className="muted">{key === "ip_allowlist" || key === "value" ? t.orgList : t.none}</span>;
    if (key === "merchant_ids" && Array.isArray(v)) return v.length ? v.map((id) => brands[String(id)] ?? shorten(String(id), 8, 4)).join(", ") : <span className="muted">{t.none}</span>;
    if (key === "all_merchants") return v ? t.allBrands : t.selectedBrands;
    if (key === "permissions" && Array.isArray(v)) return v.length ? `${v.length}: ${v.map((p) => `${String(p).split(":")[0]} ${permissionLabel(String(p)).toLowerCase()}`).join(", ")}` : t.none;
    if (Array.isArray(v)) return v.length ? <span className="mono-sm">{v.join(", ")}</span> : <span className="muted">{t.emptyAnyNetwork}</span>;
    if (typeof v === "boolean") return v ? t.yes : t.no;
    if (isObj(v)) return <code className="inline-json">{JSON.stringify(v)}</code>;
    return String(v);
  };
}

/** Compact before → after table for role / scope / allowlist changes. */
function Diff({ before, after, brands }: { before: unknown; after: unknown; brands: Record<string, string> }) {
  const t = useT().activity;
  const show = useFormatter(brands);
  // Lists (allowlists) and plain values are shown as one row.
  const b = isObj(before) ? before : { value: before };
  const a = isObj(after) ? after : { value: after };
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])];
  const same = (k: string) => JSON.stringify(b[k] ?? null) === JSON.stringify(a[k] ?? null);
  const names: Record<string, string> = t.fields;

  if (Array.isArray(b.permissions) || Array.isArray(a.permissions)) {
    const bp = (Array.isArray(b.permissions) ? b.permissions : []) as string[];
    const ap = (Array.isArray(a.permissions) ? a.permissions : []) as string[];
    const added = ap.filter((p) => !bp.includes(p));
    const removed = bp.filter((p) => !ap.includes(p));
    return (
      <div className="diff">
        {keys.filter((k) => k !== "permissions" && !same(k)).map((k) => (
          <div key={k} className="diff-row"><span className="diff-key">{names[k] ?? k}</span><span className="diff-old">{show(k, b[k])}</span><span className="diff-arrow">→</span><span className="diff-new">{show(k, a[k])}</span></div>
        ))}
        {added.length > 0 && <div className="diff-row"><span className="diff-key">{t.added}</span><span className="diff-new wide">{added.map((p) => <span key={p} className="perm-tag add">{p}</span>)}</span></div>}
        {removed.length > 0 && <div className="diff-row"><span className="diff-key">{t.removed}</span><span className="diff-old wide">{removed.map((p) => <span key={p} className="perm-tag del">{p}</span>)}</span></div>}
        {!added.length && !removed.length && keys.every(same) && <p className="muted small">{t.noChanges}</p>}
      </div>
    );
  }
  return (
    <div className="diff">
      {keys.map((k) => (
        <div key={k} className={`diff-row ${same(k) ? "unchanged" : ""}`}>
          <span className="diff-key">{names[k] ?? k}</span>
          <span className="diff-old">{show(k, b[k])}</span>
          <span className="diff-arrow">→</span>
          <span className="diff-new">{show(k, a[k])}</span>
        </div>
      ))}
    </div>
  );
}

export function Activity() {
  const t = useT();
  const { can } = useSession();
  const allowed = can("activity:view");
  const { values, setMany } = useUrlFilters(["from", "to"]);
  const list = usePagedList<ActivityItem>(allowed ? "/activity" : "", values);
  const team = useAsync(() => (can("team:view") ? api.get<TeamData>("/team") : Promise.resolve(null)), []);
  const brands = Object.fromEntries((team.data?.brands ?? []).map((b) => [b.id, b.name]));
  const [open, setOpen] = useState<ActivityItem | null>(null);

  const cols: Column<ActivityItem>[] = [
    { key: "time", header: t.activity.time, cell: (a) => <Time iso={a.created_at} />, mobile: "title" },
    { key: "who", header: t.activity.who, cell: (a) => <span className={a.actor === "API" || a.actor === "System" ? "actor-sys" : "actor"}>{a.actor}</span> },
    {
      key: "action", header: t.activity.action, cell: (a) => (
        <span className="stack-cell">
          <span>{label(a.action)} {brief(a.details) && <span className="muted">{brief(a.details)}</span>}</span>
          <code className="raw-action">{a.action}</code>
        </span>
      ),
    },
    {
      key: "target", header: t.activity.target, cell: (a) => a.target_type ? (
        <span className="stack-cell">
          <span>{targetLabel(a.target_type)}</span>
          {a.target_id && <small className="mono-sm muted" title={a.target_id}>{shorten(a.target_id, 8, 4)}</small>}
        </span>
      ) : <span className="muted">–</span>,
    },
    { key: "ip", header: t.activity.ip, cell: (a) => <span className="mono-sm muted">{a.ip ?? "–"}</span>, mobile: "hide" },
  ];

  const d = open && isObj(open.details) ? open.details : null;
  const hasDiff = !!d && ("before" in d || "after" in d);
  const rest = d ? Object.fromEntries(Object.entries(d).filter(([k]) => k !== "before" && k !== "after")) : {};

  return (
    <div className="page">
      <PageHeader title={t.activity.title}>
        {t.activity.intro}
      </PageHeader>
      {!allowed ? (
        <EmptyState title={t.activity.noAccessTitle}>{t.activity.noAccessBody}</EmptyState>
      ) : (
        <>
          <DateRange from={values.from ?? ""} to={values.to ?? ""} onChange={(from, to) => setMany({ from, to })} />
          {list.error ? (
            <Alert tone="error" action={<button className="btn btn-ghost btn-sm" onClick={() => void list.reload()}>{t.common.retry}</button>}>{errorMessage(list.error)}</Alert>
          ) : (
            <>
              <DataTable caption={t.activity.caption} columns={cols} rows={list.items} rowKey={(a) => a.id} loading={list.loading} onRowClick={setOpen}
                rowLabel={(a) => fmt(t.activity.rowLabel, { action: label(a.action), actor: a.actor })} empty={<EmptyState title={t.activity.emptyTitle} />}
                footer={<Pager page={list.page} limit={list.limit} count={list.items.length} total={list.total} hasMore={list.hasMore} onPage={list.setPage} onLimit={list.setLimit} />} />
            </>
          )}
        </>
      )}
      <Dialog open={!!open} onClose={() => setOpen(null)} kind="drawer" title={open ? label(open.action) : ""} description={open ? <code className="event-type">{open.action}</code> : undefined}>
        {open && (
          <>
            {hasDiff && d && (
              <section>
                <h3 className="drawer-sub">{t.activity.whatChanged}</h3>
                <Diff before={d.before} after={d.after} brands={brands} />
              </section>
            )}
            <Details
              rows={[
                [t.activity.who, open.actor],
                [t.activity.when, <Time iso={open.created_at} full />],
                [t.activity.target, open.target_type ? targetLabel(open.target_type) : null],
                [t.activity.ip, open.ip ? <Hash value={open.ip} full /> : null],
                [t.activity.targetId, open.target_id ? <Hash value={open.target_id} full /> : null, true],
                ...Object.entries(rest).map(([k, v]) => [detailLabel(k), typeof v === "object" ? <code className="inline-json">{JSON.stringify(v)}</code> : String(v), true] as [ReactNode, ReactNode, boolean]),
              ]}
            />
          </>
        )}
      </Dialog>
    </div>
  );
}
